import React, { useState } from "react";
import { withForm } from "@montana/shared/form/field";
import { isFieldTouched, isFieldValid } from "@montana/shared/form/field/field.component";
import { FieldWrapper } from "@montana/shared/form/field-wrapper";
import { IFieldOptionValue, ISharedFieldProperties } from "@montana/shared/form/field/field";
import { UIDropdown } from "@montana/shared/dropdown";


const placeholderValue = " ▄█▀ "; // Some unique value that would most likely never be passed to the component.



export interface ISelectProps extends ISharedFieldProperties {
    placeholder?: string;
    disabled?: boolean;
    id?: string;
    options?: IFieldOptionValue[];
    defaultValue?: string | number;
    value?: string;
    onChange: (value: string, text: string, index: number) => void;
    onBlur?: () => void;
    required?: boolean;
    hiddenLabel?: boolean;
    floatingLabel?: boolean;
    inline?: boolean;
    className?: string;
    direction?: 'up' | 'down';
}


export const Select: React.FC<ISelectProps> = React.memo((props: ISelectProps): JSX.Element => {
    const {
        placeholder,
        disabled,
        isValid,
        isEmpty,
        errorMessage,
        options = [],
        defaultValue,
        id,
        required,
        value,
        label,
        hiddenLabel,
        floatingLabel,
        inlineLabel,
        inline,
        onChange = (): void => {/* Do nothing. */},
        // onBlur = (): void => {/* Do nothing. */},
        className,
        direction,
    } = props;

    // Check if the supplied "default value" exists in the array of options.
    let defaultExists = false;
    if (defaultValue) {
        options.forEach((option): void => {
            if (!defaultExists && (
                (typeof option === "object" ? option.value === defaultValue : option === defaultValue)
            )) {
                defaultExists = true;
            }
        });
    }

    // Find out what the default selection should be.
    const selection = value || (defaultExists ? defaultValue : undefined) || placeholderValue;

    const hasNoSelection = typeof isEmpty === "boolean" ? isEmpty : selection === placeholderValue;

    const [hasBeenTouched, setHasBeenTouched] = useState(false);

    const handleChange = (value: string, text: string, index: number): void => {
        onChange(value, text, index);
        setHasBeenTouched(true);
    };

    return (
        <div
            className={
                `select${
                    disabled ? " select--disabled" : ""}${
                    selection === placeholderValue ? " select--no-selection" : ""}${
                    !isValid ? " select--invalid" : ""} ${className || ''}`
            }
        >
            <FieldWrapper
                label={!inlineLabel ? (label || placeholder || "") : ''}
                id={id || ""}
                errorMessage={errorMessage}
                isValid={isValid}
                isTouched={hasBeenTouched}
                isEmpty={hasNoSelection}
                hiddenLabel={hiddenLabel}
                inlineLabel={false}
                floatingLabel={floatingLabel}
            >
                <UIDropdown
                    options={options}
                    onChange={handleChange}
                    disabled={disabled}
                    value={selection}
                    id={id}
                    className={className}
                    required={required}
                    placeholder={placeholder}
                    inline={inline}
                    label={inlineLabel ? label : undefined}
                    direction={direction}
                />
            </FieldWrapper>
        </div>
    );
});



export const FormSelect = withForm<
ISelectProps,
"value" | "onChange" | "isValid" | "onBlur" | "isEmpty" | "isTouched" | "errorMessage"
>(Select, (field, form): ISelectProps => ({
    value: field.value,
    onChange: (value: string): void => form.setFieldValue(field.name, value),
    isValid: isFieldValid(field.name, form),
    isEmpty: !field.value,
    errorMessage: (form.errors as { [key: string ]: string })[field.name],
    onBlur: (): void => form.setFieldTouched(field.name),
    isTouched: isFieldTouched(field.name, form),
}));
